import type React from "react";
import { twMerge } from "tailwind-merge";
import { Icon } from "../Icon";

export interface MapPlaceholderProps {
  /**
   * Container style
   */
  style?: React.CSSProperties;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Custom message to display
   * @default "Map requires Mapbox token"
   */
  message?: string;
  /**
   * Render a designed **schematic** map — a tokenized grid pattern with a
   * centered pin and a soft radius circle — instead of the bare
   * "Map requires Mapbox token" message. Additive: default `false` keeps the
   * message so existing consumers are unchanged. Use this where a tokenless
   * environment should still look designed (e.g. the entity Location card).
   * @default false
   */
  schematic?: boolean;
  /**
   * On the schematic variant, show the centered pin + soft radius marker.
   * Ignored by the message variant.
   * @default true
   */
  showMarker?: boolean;
}

// Tokenized schematic grid — thin map-neutral lines at low opacity over the
// muted surface. `color-mix` keeps this token-driven (no hex) and re-themes with
// light/dark. Two gradients draw the vertical + horizontal rules; the 24px tile
// gives a calm graph-paper feel rather than a dense mesh.
const SCHEMATIC_GRID_LINE = "color-mix(in srgb, var(--color-map-neutral-border) 8%, transparent)";
const SCHEMATIC_GRID_STYLE: React.CSSProperties = {
  backgroundImage: `linear-gradient(to right, ${SCHEMATIC_GRID_LINE} 1px, transparent 1px), linear-gradient(to bottom, ${SCHEMATIC_GRID_LINE} 1px, transparent 1px)`,
  backgroundSize: "24px 24px",
};

// Soft radius halo under the pin — the marker token at ~12% so it reads as a
// gentle "coverage" glow, not a solid dot.
const SCHEMATIC_RADIUS_STYLE: React.CSSProperties = {
  backgroundColor: "color-mix(in srgb, var(--color-map-marker-default) 12%, transparent)",
};

/**
 * MapPlaceholder
 *
 * Placeholder component shown when a Mapbox token is not available.
 * Used by StaticMap and InteractiveMap when the token is not set.
 *
 * Two variants:
 *   - default — a centered pin + "Map requires Mapbox token" message.
 *   - `schematic` — a designed grid + centered pin + soft radius circle, so a
 *     tokenless environment still renders a map-shaped, on-brand surface.
 */
export function MapPlaceholder({
  style,
  className,
  message = "Map requires Mapbox token",
  schematic = false,
  showMarker = true,
}: MapPlaceholderProps) {
  if (schematic) {
    return (
      <div
        style={style}
        data-testid="map-schematic"
        className={twMerge("relative overflow-hidden border border-map-neutral-border bg-background-muted", className)}
      >
        {/* Grid pattern */}
        <div aria-hidden="true" className="absolute inset-0" style={SCHEMATIC_GRID_STYLE} />
        {showMarker ? (
          <div className="absolute inset-0 flex items-center justify-center">
            {/* Soft radius circle */}
            <span aria-hidden="true" className="absolute h-24 w-24 rounded-full" style={SCHEMATIC_RADIUS_STYLE} />
            {/* Centered pin */}
            <Icon name="MapPin" size={36} weight="fill" className="relative text-map-marker-default" />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      style={style}
      className={twMerge("flex items-center justify-center bg-background-muted text-text-secondary", className)}
    >
      <div className="text-center">
        <Icon name="MapPin" size={48} className="mx-auto mb-2 opacity-50" />
        <p className="text-sm">{message}</p>
      </div>
    </div>
  );
}
